import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
} from '@utils/renderStoryblokRichText';

import { useUnitOfMeasurement } from '../../../hooks/useUnitOfMeasurement';
import {
  DigitalDimensionsStoryblok,
  DimensionsStoryblok,
  ProductDimensionsStoryblok,
  RichtextStoryblok,
} from '../../../types/generated-storyblok';
import { UnitsOfMeasure } from '../../../types/Locations';
import { cmToIn } from '../../../utils/product';

const isDimensionDigital = (
  dimension: DimensionsStoryblok | DigitalDimensionsStoryblok,
): dimension is DigitalDimensionsStoryblok =>
  dimension.component === 'digitalDimensions';

const getDimensionsFormat = (
  dimension: DimensionsStoryblok | DigitalDimensionsStoryblok,
  unit: UnitsOfMeasure | 'PX',
): {
  displayString: string;
  displayStringAlt?: RichtextStoryblok;
} => {
  const isDigital = isDimensionDigital(dimension);

  const hasDiameter = isDigital ? false : dimension.diameter || false;
  const height = parseFloat(
    isDigital ? dimension.heightInPixels : dimension.heightCM || '0',
  );
  const width = parseFloat(
    isDigital ? dimension.widthInPixels : dimension.widthCM || '0',
  );
  const depth = isDigital ? 0 : parseFloat(dimension.depthCM || '0');
  const freeText = isDigital ? undefined : dimension?.FreeTextDimensions;

  const dimensions = {
    height: unit === 'IN' ? cmToIn(height) : height,
    width: unit === 'IN' ? cmToIn(width) : width,
    depth: unit === 'IN' ? cmToIn(depth) : depth,
  };

  const relevantDimensions = Object.fromEntries(
    Object.entries(dimensions).filter(([, value]) => value !== 0),
  );

  const displayNames = {
    height: 'H',
    width: 'W',
    depth: 'D',
  } as const;

  const displayUnits = {
    IN: 'in',
    CM: 'cm',
    PX: 'px',
  } as const;

  const displayDimensions = Object.entries(relevantDimensions)
    .map(([key, value]) => {
      const dimensionKey = key as keyof typeof displayNames;
      return `${value.toFixed(unit === 'PX' ? 0 : 2)}${displayUnits[unit]} (${
        displayNames[dimensionKey]
      })`;
    })
    .join(' × ');

  return {
    displayString: hasDiameter ? `⌀ ${displayDimensions}` : displayDimensions,
    displayStringAlt: freeText,
  };
};

const Dimensions = ({
  dimensions,
}: {
  dimensions: ProductDimensionsStoryblok;
}) => {
  const [unitMeasurement] = useUnitOfMeasurement();

  const unframedDims = dimensions?.unframed?.[0]
    ? getDimensionsFormat(dimensions?.unframed?.[0], unitMeasurement)
    : null;

  const framedDims = dimensions?.framed?.[0]
    ? getDimensionsFormat(dimensions?.framed?.[0], unitMeasurement)
    : null;

  const defaultDims = dimensions?.default?.[0]
    ? getDimensionsFormat(dimensions?.default?.[0], unitMeasurement)
    : null;

  const digitalDims = dimensions?.digital?.[0]
    ? getDimensionsFormat(dimensions?.digital?.[0], 'PX')
    : null;

  return (
    <div className="flex flex-col">
      {(isStoryblokRichTextPresent(unframedDims?.displayStringAlt) &&
        renderStoryblokRichText(unframedDims?.displayStringAlt)) ||
        (unframedDims?.displayString ? (
          <p>Unframed: {unframedDims?.displayString}</p>
        ) : null) ||
        null}

      {(isStoryblokRichTextPresent(framedDims?.displayStringAlt) &&
        renderStoryblokRichText(framedDims?.displayStringAlt)) ||
        (framedDims?.displayString ? (
          <p>Framed: {framedDims?.displayString}</p>
        ) : null) ||
        null}

      {(isStoryblokRichTextPresent(defaultDims?.displayStringAlt) &&
        renderStoryblokRichText(defaultDims?.displayStringAlt)) ||
        (defaultDims?.displayString ? (
          <p>{defaultDims?.displayString}</p>
        ) : null) ||
        null}

      {(isStoryblokRichTextPresent(digitalDims?.displayStringAlt) &&
        renderStoryblokRichText(digitalDims?.displayStringAlt)) ||
        (digitalDims?.displayString ? (
          <p>{digitalDims?.displayString}</p>
        ) : null) ||
        null}
    </div>
  );
};

export default Dimensions;
